import React, { lazy, Suspense, useEffect, useState } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import theme from './theme';
import FuturisticNavbar from './components/FuturisticNavbar';
import FuturisticFooter from './components/FuturisticFooter';
import LoadingSpinner from './components/LoadingSpinner';
import ScrollToTop from './components/ScrollToTop';

// ⚡ PERFORMANCE: Lazy load all pages for code splitting
const FuturisticHome = lazy(() => import('./pages/FuturisticHome'));
const ManagedITServicesAndHelpDesk = lazy(() => import('./pages/ManagedITServicesAndHelpDesk'));
const Cybersecurity = lazy(() => import('./pages/Cybersecurity'));
const SoftwareSolutions = lazy(() => import('./pages/SoftwareSolutions'));
const BackupDisasterRecovery = lazy(() => import('./pages/BackupDisasterRecovery'));
const CRMSolutions = lazy(() => import('./pages/CRMSolutions'));
const Dynamics365 = lazy(() => import('./pages/Dynamics365'));
const Microsoft365Azure = lazy(() => import('./pages/Microsoft365Azure'));
const HowWeWork = lazy(() => import('./pages/HowWeWork'));
const NetworkCabling = lazy(() => import('./pages/NetworkCabling'));
const CloudMigration = lazy(() => import('./pages/CloudMigration'));
const InfrastructureAndServerManagement = lazy(() => import('./pages/InfrastructureAndServerManagement'));
const VoIPCommunications = lazy(() => import('./pages/VoIPCommunications'));
const AIAutomation = lazy(() => import('./pages/AIAutomation'));
const GeneralConsultation = lazy(() => import('./pages/GeneralConsultation'));
const AboutUs = lazy(() => import('./pages/AboutUs'));
const Contact = lazy(() => import('./pages/Contact'));
const ContactSuccess = lazy(() => import('./pages/ContactSuccess'));
const ExistingCustomer = lazy(() => import('./pages/ExistingCustomer'));
const CustomerDashboard = lazy(() => import('./pages/CustomerDashboard'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const RequestService = lazy(() => import('./pages/RequestService'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));

// While the first page's code loads, keep showing the static shell from the HTML
// (same look as the page header) instead of a spinner. Later navigations use the spinner.
let shellShown = false;
const PageFallback: React.FC = () => {
  const html = (window as Window & { __SHELL_HTML__?: string }).__SHELL_HTML__;
  useEffect(() => () => {
    shellShown = true;
  }, []);
  if (!shellShown && html) {
    return <div dangerouslySetInnerHTML={{ __html: html }} />;
  }
  return <LoadingSpinner />;
};

// The chat assistant isn't needed for the first paint, so it loads once the browser is idle.
const Chatbot = lazy(() => import('./components/Chatbot'));

const useIdle = (fallbackMs = 2500) => {
  const [idle, setIdle] = useState(false);
  useEffect(() => {
    const w = window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number };
    if (w.requestIdleCallback) {
      w.requestIdleCallback(() => setIdle(true), { timeout: fallbackMs });
    } else {
      const t = window.setTimeout(() => setIdle(true), fallbackMs);
      return () => window.clearTimeout(t);
    }
  }, [fallbackMs]);
  return idle;
};

function App() {
  const idle = useIdle();

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <ScrollToTop />
        <FuturisticNavbar />
        <main id="main-content">
        <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/" element={<FuturisticHome />} />
            <Route path="/services/managed-it-services" element={<ManagedITServicesAndHelpDesk />} />
            <Route path="/services/cybersecurity" element={<Cybersecurity />} />
            <Route path="/services/software-solutions" element={<SoftwareSolutions />} />
            <Route path="/services/backup-disaster-recovery" element={<BackupDisasterRecovery />} />
            <Route path="/services/dynamics-365" element={<Dynamics365 />} />
            <Route path="/services/microsoft-365-azure" element={<Microsoft365Azure />} />
            <Route path="/how-we-work" element={<HowWeWork />} />
            <Route path="/services/crm-solutions" element={<CRMSolutions />} />
            <Route path="/services/network-cabling" element={<NetworkCabling />} />
            <Route path="/services/cloud-migration" element={<CloudMigration />} />
            <Route path="/services/infrastructure-management" element={<InfrastructureAndServerManagement />} />
            <Route path="/services/voip-communications" element={<VoIPCommunications />} />
            <Route path="/services/ai-automation" element={<AIAutomation />} />
            <Route path="/general-consultation" element={<GeneralConsultation />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/contact-success" element={<ContactSuccess />} />
            <Route path="/customer-portal" element={<ExistingCustomer />} />
            <Route path="/existing-customer" element={<ExistingCustomer />} />
            <Route path="/customer-dashboard" element={<CustomerDashboard />} />
            <Route path="/user-management" element={<UserManagement />} />
            <Route path="/request-service" element={<RequestService />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
          </Routes>
        </Suspense>
        </main>
        <FuturisticFooter />
        {idle && (
          <Suspense fallback={null}>
            <Chatbot />
          </Suspense>
        )}
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
